import type { TranslatedField } from 'admin-panel/constants/types';
import { splitPageHtmlPrefix } from 'admin-panel/utils/page-html-prefix';
import { escapeHtml } from 'admin-panel/utils/wysiwyg-editor/html';

export type PageHeaderTitleMode = 'inherited' | 'custom' | 'unmanaged';

export type PageHeaderTitleModeTranslations = Partial<
  Record<string, PageHeaderTitleMode>
>;

export type PageHeaderTitleTranslations = Partial<Record<string, string>>;

export type PageHeaderTitlePrefixTranslations = Partial<Record<string, string>>;

export type ParsedPageHeaderTitle = {
  mode: PageHeaderTitleMode;
  headerTitle: string | null;
  prefixHtml: string;
  bodyHtml: string;
};

const NO_PAGE_HEADER_TITLE_COMMENT = 'admin-panel-page-header-title:none';

export const NO_PAGE_HEADER_TITLE_HTML = `<!-- ${NO_PAGE_HEADER_TITLE_COMMENT} -->`;

const LEADING_PLAIN_H1_PATTERN = /^<h1\s*>[\s\S]*?<\/h1\s*>/i;

function normalizedVisibleTitle(value: string) {
  return value.replace(/[\t\n\f\r ]+/g, ' ').replace(/^ | $/g, '');
}

function splitLeadingPrefix(html: string) {
  let prefixHtml = '';
  const { tokens, remainingHtml } = splitPageHtmlPrefix(html);

  for (const token of tokens) {
    if (isNoPageHeaderTitleMarker(token)) {
      return {
        prefixHtml,
        remainingHtml: html.slice(prefixHtml.length + token.length),
        hasNoTitleMarker: true,
      };
    }

    prefixHtml += token;
  }

  return { prefixHtml, remainingHtml, hasNoTitleMarker: false };
}

function isNoPageHeaderTitleMarker(token: string) {
  const template = document.createElement('template');
  template.innerHTML = token;
  const [comment, ...remainder] = template.content.childNodes;

  return (
    comment?.nodeType === Node.COMMENT_NODE &&
    remainder.length === 0 &&
    comment.nodeValue?.trim() === NO_PAGE_HEADER_TITLE_COMMENT
  );
}

function isPlainTextHeading(element: HTMLElement) {
  return (
    element.tagName === 'H1' &&
    element.attributes.length === 0 &&
    [...element.childNodes].every((node) => node.nodeType === Node.TEXT_NODE)
  );
}

function matchingSourceHeading(html: string, candidate: HTMLElement) {
  // `outerHTML` cannot prove this boundary because parsing normalizes legacy
  // source casing and spacing such as `<H1 >`. Match the raw source, then
  // verify that it parses back to exactly the validated candidate instead.
  const source = html.match(LEADING_PLAIN_H1_PATTERN)?.[0];
  if (!source) return null;

  const template = document.createElement('template');
  template.innerHTML = source;
  const [sourceCandidate, ...remainder] = template.content.childNodes;

  return sourceCandidate instanceof HTMLElement &&
    remainder.length === 0 &&
    sourceCandidate.isEqualNode(candidate)
    ? source
    : null;
}

export function parsePageHeaderTitle(
  html: string,
  navigationTitle: string
): ParsedPageHeaderTitle {
  const { prefixHtml, remainingHtml, hasNoTitleMarker } =
    splitLeadingPrefix(html);

  if (hasNoTitleMarker) {
    return {
      mode: 'custom',
      headerTitle: '',
      prefixHtml,
      bodyHtml: remainingHtml,
    };
  }

  const template = document.createElement('template');
  template.innerHTML = remainingHtml;
  const candidate = template.content.firstChild;

  if (!(candidate instanceof HTMLElement) || candidate.tagName !== 'H1') {
    if (template.content.querySelector('h1')) {
      return {
        mode: 'unmanaged',
        headerTitle: null,
        prefixHtml: '',
        bodyHtml: html,
      };
    }

    return {
      mode: 'inherited',
      headerTitle: null,
      prefixHtml,
      bodyHtml: remainingHtml,
    };
  }

  if (!isPlainTextHeading(candidate)) {
    return {
      mode: 'unmanaged',
      headerTitle: null,
      prefixHtml: '',
      bodyHtml: html,
    };
  }

  const headerTitle = candidate.textContent ?? '';
  const sourceHeading = matchingSourceHeading(remainingHtml, candidate);

  if (!sourceHeading) {
    return {
      mode: 'unmanaged',
      headerTitle: null,
      prefixHtml: '',
      bodyHtml: html,
    };
  }

  const bodyHtml = remainingHtml.slice(sourceHeading.length);
  const mode =
    normalizedVisibleTitle(headerTitle) ===
    normalizedVisibleTitle(navigationTitle)
      ? 'inherited'
      : 'custom';

  return {
    mode,
    headerTitle: mode === 'custom' ? headerTitle : null,
    prefixHtml,
    bodyHtml,
  };
}

export function pageHeaderTitleModeFor(
  translations: PageHeaderTitleModeTranslations,
  localeKey: string
): PageHeaderTitleMode {
  return translations[localeKey] ?? 'inherited';
}

export function splitPageHeaderTitleTranslations(
  contentTranslations: TranslatedField,
  titleTranslations: TranslatedField
): {
  headerTitleModeTranslations: PageHeaderTitleModeTranslations;
  headerTitleTranslations: PageHeaderTitleTranslations;
  headerTitlePrefixTranslations: PageHeaderTitlePrefixTranslations;
  contentTranslations: TranslatedField;
} {
  const headerTitleModeTranslations: PageHeaderTitleModeTranslations = {};
  const headerTitleTranslations: PageHeaderTitleTranslations = {};
  const headerTitlePrefixTranslations: PageHeaderTitlePrefixTranslations = {};
  const bodyTranslations: TranslatedField = {};

  for (const [locale, content] of Object.entries(contentTranslations)) {
    if (content === null) {
      bodyTranslations[locale] = null;
      continue;
    }

    const parsed = parsePageHeaderTitle(
      content,
      titleTranslations[locale] ?? ''
    );

    if (parsed.mode !== 'inherited') {
      headerTitleModeTranslations[locale] = parsed.mode;
    }

    if (parsed.mode === 'custom') {
      headerTitleTranslations[locale] = parsed.headerTitle ?? '';
    }

    if (parsed.prefixHtml) {
      headerTitlePrefixTranslations[locale] = parsed.prefixHtml;
    }

    bodyTranslations[locale] = parsed.bodyHtml;
  }

  return {
    headerTitleModeTranslations,
    headerTitleTranslations,
    headerTitlePrefixTranslations,
    contentTranslations: bodyTranslations,
  };
}

export function composePageHeaderTitle(
  content: string | null | undefined,
  navigationTitle: string,
  mode: PageHeaderTitleMode,
  customTitle = '',
  prefixHtml = ''
) {
  if (mode === 'unmanaged') return content ?? null;

  const title = mode === 'custom' ? customTitle : navigationTitle;
  const headerTitleHtml =
    mode === 'custom' && !normalizedVisibleTitle(title)
      ? NO_PAGE_HEADER_TITLE_HTML
      : `<h1>${escapeHtml(title)}</h1>`;

  return `${prefixHtml}${headerTitleHtml}${content ?? ''}`;
}
